import React, { Component } from "react";
import ReactGA from "react-ga";
import { Link } from "react-router-dom";
import BannerImg from "../../assets/images/banner/management.png";
import { Helmet } from "react-helmet";
import JsonLd from "../../components/seo/json-ld";

const executivePlaceholders = [
  {
    title: "Managing Director",
    text: "Profile details can be completed here once a verified biography or approved photo is available.",
  },
  {
    title: "Delivery Governance",
    text: "A future profile can capture the person responsible for program oversight, risk, reporting and quality.",
  },
  {
    title: "International Delivery",
    text: "A future profile can represent the leader guiding offshore collaboration and multi-region execution.",
  },
];

class Management extends Component {
  state = {};
  render() {
    ReactGA.initialize("UA-5235714-2");
    ReactGA.pageview(window.location.pathname + window.location.search);

    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Avani Cimcon Technologies",
        url: "https://www.avanicimcon.com/",
        description:
          "Enterprise software engineering company with long-term delivery experience, modernization capability and global project history.",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://www.avanicimcon.com/" },
          { "@type": "ListItem", position: 2, name: "Company", item: "https://www.avanicimcon.com/about" },
          { "@type": "ListItem", position: 3, name: "Management", item: "https://www.avanicimcon.com/management" },
        ],
      },
    ];

    return (
      <React.Fragment>
        <Helmet defer={false}>
          <title>Management | Leadership, Governance & Enterprise Delivery | Avani Cimcon</title>
          <meta
            name="keywords"
            content="management, leadership, enterprise delivery, delivery governance, enterprise software engineering, legacy modernization, ai enablement, international delivery"
          />
          <meta
            name="description"
            content="Leadership at Avani Cimcon combines enterprise software experience, modernization discipline, delivery governance and international execution history."
          />
          <link rel="canonical" href="https://www.avanicimcon.com/management" />
          <meta property="og:type" content="website" />
          <meta property="og:url" content="https://www.avanicimcon.com/management" />
          <meta property="og:title" content="Management | Leadership, Governance & Enterprise Delivery | Avani Cimcon" />
          <meta
            property="og:description"
            content="Leadership at Avani Cimcon combines enterprise software experience, modernization discipline, delivery governance and international execution history."
          />
          <meta property="og:image" content="https://www.avanicimcon.com/images/banner/management.png" />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content="Management | Leadership, Governance & Enterprise Delivery | Avani Cimcon" />
          <meta
            name="twitter:description"
            content="Leadership at Avani Cimcon combines enterprise software experience, modernization discipline, delivery governance and international execution history."
          />
          <meta name="twitter:image" content="https://www.avanicimcon.com/images/banner/management.png" />
        </Helmet>
        <JsonLd data={schemas} />

        <div className="page-banner d-flex align-items-center justify-content-center text-white">
          <div className="page-title container position-relative">
            <h1 className="font-weight-bold text-uppercase">Leadership & Delivery Governance</h1>
            <h5>Engineering leadership with modernization and international delivery discipline</h5>
          </div>
          <div className="page-banner-img" style={{ backgroundImage: `url("${BannerImg}")` }} />
          <div className="page-banner-overlay" />
        </div>

        <div className="inner-page mt-5 mb-5 ">
          <div className="container">
            <div className="text-justify">
              <p>
                Avani Cimcon's management story is shaped by long-term enterprise software delivery.
                The leadership perspective has to work across architecture, support, modernization
                and AI enablement without losing the judgment that enterprise buyers rely on.
              </p>
              <p>
                The company has operated across technology eras and delivery models, so the
                management page focuses on what that experience means in practice: clear leadership
                philosophy, stable governance, international collaboration and a modernization
                approach that protects business continuity.
              </p>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">Leadership philosophy</h4>
              <p>
                Leadership at Avani Cimcon is grounded in enterprise reality. The work is not about
                chasing trends first and asking operational questions later. It is about helping
                organizations improve the systems they already depend on while keeping delivery
                predictable and accountable.
              </p>
              <p className="mb-0">
                That philosophy aligns naturally with the company’s broader positioning around{" "}
                <Link to="/services/legacy-modernization">legacy modernization</Link>,{" "}
                <Link to="/services/managed-services">managed services</Link>, and{" "}
                <Link to="/services/enterprise-ai-enablement">enterprise AI enablement</Link>.
              </p>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">Enterprise software experience</h4>
              <p>
                The organization’s experience includes early accounting systems, ERP for transport
                and textile operations, offshore project delivery, workflow systems, content
                platforms, booking systems, integration layers, product engineering and AI-aware
                thinking. That breadth matters because enterprise software decisions rarely live in
                only one discipline.
              </p>
              <p className="mb-0">
                To see how that experience was built, review the{" "}
                <Link to="/about">company history</Link> and{" "}
                <Link to="/expertise-experience">enterprise expertise pages</Link>.
              </p>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">Modernization approach</h4>
              <p>
                Avani Cimcon does not frame modernization as a rewrite by default. The preferred
                approach is to assess the estate, preserve the business logic, stabilize the system,
                modernize in manageable steps, integrate the newer components and enable AI where it
                adds measurable value.
              </p>
              <p className="mb-0">
                That same framework is used across the{" "}
                <Link to="/services/legacy-modernization">legacy modernization service page</Link>{" "}
                and the{" "}
                <Link to="/enterprise-ai">enterprise AI overview</Link>.
              </p>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">Enterprise AI perspective</h4>
              <p>
                AI is treated as an operational layer rather than a standalone claim. The management
                narrative therefore supports AI only when the surrounding system is stable enough to
                absorb it: integrated data, clear governance, quality delivery and a business
                problem worth solving.
              </p>
              <p className="mb-0">
                See the <Link to="/enterprise-ai">AI hub</Link> and{" "}
                <Link to="/services/enterprise-ai-enablement">enterprise AI enablement page</Link>{" "}
                for the full positioning.
              </p>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">Delivery governance</h4>
              <p>
                Governance is part of the service, not a wrapper around it. The management model
                emphasizes planning, reporting, quality assurance and controlled implementation so
                that enterprise stakeholders can see what is happening and why.
              </p>
              <ul className="mb-0">
                <li className="mb-2">Program visibility and release discipline</li>
                <li className="mb-2">Quality systems aligned to long-term support</li>
                <li className="mb-2">Risk-aware change management</li>
                <li>Enterprise communication across business and technical stakeholders</li>
              </ul>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">International delivery experience</h4>
              <p>
                The company has delivered across the USA, UK, Asia Pacific, Australia, New Zealand,
                the Middle East and Europe. That international footprint matters because the
                management layer must be able to support clients, teams and stakeholders across time
                zones and operating cultures.
              </p>
              <p className="mb-0">
                For supporting proof, review the{" "}
                <Link to="/international-projects">international projects</Link>,{" "}
                <Link to="/international-acceptance">international acceptance</Link> and{" "}
                <Link to="/membership">membership history</Link> pages.
              </p>
            </div>

            <div className="shadow-sm p-3 mt-4">
              <h4 className="text-primary mb-3 font-weight-bold">Executive profiles</h4>
              <p className="mb-4">
                The business has not published verified bios and photos for every current executive
                in this source set. These placeholders are ready for completion once approved
                profiles are available.
              </p>
              <div className="row">
                {executivePlaceholders.map((item) => (
                  <div key={item.title} className="col-lg-4 mb-4">
                    <article className="border rounded p-3 h-100 bg-light">
                      <h5 className="text-primary font-weight-bold">{item.title}</h5>
                      <p className="mb-0">{item.text}</p>
                    </article>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </React.Fragment>
    );
  }
}

export default Management;
